import { Component, OnInit, Input, OnChanges, OnDestroy } from '@angular/core'
import { ActionService } from '../_services';
import { Router, NavigationExtras } from "@angular/router";
import { SubmissionParams } from '../_models/session';

import { ThemeService } from '../_helpers/theming-engine/theme.service';
import { ErrorModalHelperService } from '../_helpers/error-modal-helper/error-modal-helper.service';
import { TutorReportsHelperService } from '../_helpers/tutor-reports-helper/tutor-reports-helper.service';
import { ColumnDef, DEFAULT_PAGE_SIZE, PageChange } from '../shared/data-table/data-table.model';
import { PagedResponse } from '../_models/common/paged-response.model';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { BrandFilterService } from '../_services/brand-filter.service';
import { BrandInfo } from '../_models/reports/brand-info.model';
import { FeatureSwitchService } from '../_services/feature-switch/feature-switch.service';
import { FeatureSwitchKeys } from '../_constants/feature-switch-keys';

@Component({
    selector: 'missing-submissions',
    templateUrl: 'missing.submissions.component.html',
    styleUrls: ['missing.submissions.component.css'],
    standalone: false
})

export class MissingSubmissionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() displayall: boolean = false;
  @Input() showHeader: boolean = false;
  @Input() submissionParams: SubmissionParams = null;
  @Input() pageheader: string;
  @Input() currDate: string;
  /** Brands for the paged landing grid; empty means every brand the user may see. */
  @Input() companyIds: number[] = [];
  rows: any[] = [];
  columns: ColumnDef[] = [];
  /** The user's brands; the Brand column shows only when there is more than one. */
  brands: BrandInfo[] = [];
  /**
   * The filters.missing-submissions switch. Off: no Brand column, the brand list is never fetched,
   * and the paged landing request carries no companyIds.
   */
  filtersEnabled: boolean = false;

  /**
   * Server-side paging, used only when `displayall` is true: the landing grid composes thousands of
   * rows, so only one page is fetched/rendered. The `/missing` search returns a bounded array and is
   * paged client-side by the table, so `totalCount` is not used on that path.
   */
  public totalCount: number = 0;
  public pageNumber: number = 1;
  public pageSize: number = DEFAULT_PAGE_SIZE;
  private readonly fetchTrigger = new Subject<void>();
  private readonly destroyed = new Subject<void>();
  header: string = 'Missing Tutor Submissions';
  loading: boolean = false;
  noData: boolean = false;

  constructor(
    private actionService: ActionService,
    private router: Router,
    public themeService: ThemeService,
    private errorModalHelper: ErrorModalHelperService,
    private tutorReportsHelperService: TutorReportsHelperService,
    private brandFilterService: BrandFilterService,
    private featureSwitches: FeatureSwitchService
    ) {
  }

  ngOnInit() {
    this.buildColumns();

    this.fetchTrigger.pipe(
      switchMap(() => {
        this.loading = true;
        return this.actionService.getMissingSubmissionsFast(this.buildRequest());
      }),
      takeUntil(this.destroyed)
    ).subscribe((page: PagedResponse<any>) => {
      this.loading = false;
      this.assignPage(page);
    }, error => {
      this.loading = false;
      this.errorModalHelper.show(error.message, error);
    });

    if (this.pageheader != null) {
      this.header = this.pageheader;
    }
    // The switch decides whether the landing fetch may carry companyIds, so it resolves first.
    this.featureSwitches.isEnabled(FeatureSwitchKeys.filtersMissingSubmissions).pipe(
      takeUntil(this.destroyed)
    ).subscribe(enabled => {
      this.filtersEnabled = enabled;
      if (enabled) {
        this.brandFilterService.getMyBrands().pipe(takeUntil(this.destroyed)).subscribe(brands => {
          this.brands = brands ?? [];
          this.buildColumns();
        });
      }
      if (this.displayall) {
        this.fetchTrigger.next();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  /** Fetches the page the pager asked for, at the chosen size. */
  onPageChange(change: PageChange): void {
    this.pageNumber = change.page;
    this.pageSize = change.pageSize;
    this.fetchTrigger.next();
  }

  /** Opt-in paging (§7c): `paged` makes the API return an envelope instead of a bare array. */
  private buildRequest(): { pageNumber: number; pageSize: number; companyIds?: number[] } {
    // No userEmail scoping (confirmed 2026-08-10). Brand is the only filter, and only behind the switch.
    const request: { pageNumber: number; pageSize: number; companyIds?: number[] } = { pageNumber: this.pageNumber, pageSize: this.pageSize };
    if (this.filtersEnabled) {
      request.companyIds = this.companyIds ?? [];
    }
    return request;
  }

  /** Takes rows from the envelope; totalCount drives the footer, never rows.length. */
  private assignPage(page: PagedResponse<any>): void {
    this.rows = page && page.items ? page.items : [];
    this.totalCount = page ? page.totalCount : 0;
    this.noData = this.totalCount === 0;
  }

  private buildColumns(): void {
    const studentHeader = 'Learner';
    const columns: ColumnDef[] = [
      { field: 'sessionStartDate', header: 'Date', type: 'date', width: '12%' }
    ];
    if (this.filtersEnabled && this.brands.length > 1) {
      columns.push({ field: 'companyId', header: 'Brand', type: 'template', templateName: 'brand', width: '8%' });
    }
    this.columns = [
      ...columns,
      { field: 'tutorFullName', header: 'Tutor', width: '15%' },
      { field: 'student', header: studentHeader, type: 'template', templateName: 'student', width: '15%' },
      { field: 'client', header: 'Client', width: '20%' },
      { field: 'time', header: 'Time', type: 'template', templateName: 'time' },
      { field: 'timeSheet', header: 'Attendance', type: 'template', templateName: 'timeSheet', align: 'center' },
      { field: 'session', header: 'Session', type: 'template', templateName: 'session', align: 'center' },
      { field: 'outcome', header: 'Progress Report', type: 'template', templateName: 'outcome', align: 'center' }
    ];
  }

  ngOnChanges() {
    if (this.submissionParams !== null) {
      this.loading = true;
      this.actionService.getMissingSubmissionsSearch(this.submissionParams).pipe(
        takeUntil(this.destroyed)
      ).subscribe(data => {
        this.loading = false;
        this.assignData(data);
      }, error => {
        this.loading = false;
        this.errorModalHelper.show(error.message, error);
      });
    }

    this.header = this.showHeader ? this.pageheader == null ? 'Missing Tutor Submissions' : this.pageheader : '';
  }

  onDataclicked(row) {
    this.tutorReportsHelperService.setCommonDataBeforeNavigate(row.placementID, row.sessionStartDate, row.sessionEndDate);
    setTimeout(() => {
      this.router.navigate(["/tutorreports", row.placementID]);
    }, 200);
  }

  assignData(data) {
    this.rows = data;
    this.noData = data.length === 0;
  }
}
